// Deterministic color assignment for tiled-view function names.
// Same name -> same color everywhere, regardless of the floor or view.
// A few names are reserved to neutral, meaningful colors.

/** Reserved colors for special, non-tenant functions (case-insensitive). */
const SPECIAL_COLORS: Record<string, string> = {
  vacant: "#9ca3af", // gray-400 — empty space
  common: "#d1d5db", // gray-300 — shared/common space
};

/**
 * Convert an HSL triple to a hex string.
 */
function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n: number) => {
    const color = lN - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Build a stable name -> color map for a set of function names.
 * Non-special names are spread around the hue wheel using the golden angle
 * so adjacent slices stay visually distinct. The map is ordered by first
 * appearance to keep colors stable as long as the dataset order is stable.
 */
export function buildColorMap(names: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  let hueIndex = 0;
  const GOLDEN_ANGLE = 137.508;

  names.forEach((rawName) => {
    if (map[rawName] !== undefined) return;
    const key = rawName.trim().toLowerCase();
    if (SPECIAL_COLORS[key]) {
      map[rawName] = SPECIAL_COLORS[key];
      return;
    }
    const hue = (hueIndex * GOLDEN_ANGLE) % 360;
    // Curated, slightly muted jewel tones — cohesive rather than neon, while
    // still keeping adjacent slices distinct via the golden-angle hue spread.
    const sat = 46 + (hueIndex % 3) * 8; // 46, 54, 62
    const light = 50 + (hueIndex % 2) * 7; // 50, 57
    map[rawName] = hslToHex(hue, sat, light);
    hueIndex += 1;
  });

  return map;
}

/**
 * Pick a readable text color (black/white) for a given hex background.
 */
export function contrastTextColor(hex: string): string {
  const c = hex.replace("#", "");
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  // Perceived luminance.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? "#1f2937" : "#ffffff";
}
